import { getHierarchyTlfType } from "../getHierarchyTlfType";
import { HierarchyContextProps, IHierarchyActionLoadClinicalLibrary } from "../interface";
import { mergeClinicalLibraryData } from "../mergeClinicalLibraryData";

/**
 * Loads clinical library data into matching hierarchy nodes and parent templates.
 *
 * @param state - The current hierarchy context state.
 * @param action - The load-clinical-library action to apply.
 * @returns The updated hierarchy context state.
 */
export const hcr_loadClinicalLibrary = (
  state: HierarchyContextProps,
  action: IHierarchyActionLoadClinicalLibrary,
): HierarchyContextProps => {
  if (action.clinicalLibrary.length === 0) {
    return {
      ...state,
      isClinicalLibraryLoading: false,
    };
  }

  const updatedHierarchyNodes = state.hierarchyNodes.map((hn) => {
    const matchingLibrary = action.clinicalLibrary.find((cl) => cl.hierarchyId === hn.id);
    if (matchingLibrary) {
      return {
        ...hn,
        clinicalLibrary: matchingLibrary,
      };
    }

    return hn;
  });

  const matchingHierarchyLibrary = state.hierarchy
    ? action.clinicalLibrary.find((cl) => cl.hierarchyId === state.hierarchy?.id)
    : undefined;

  const updatedHierarchy =
    state.hierarchy && matchingHierarchyLibrary
      ? {
          ...state.hierarchy,
          clinicalLibrary: matchingHierarchyLibrary,
        }
      : state.hierarchy;

  if (!state.hierarchy) {
    return {
      ...state,
      hierarchyNodes: updatedHierarchyNodes,
      isClinicalLibraryLoading: false,
    };
  }

  const currentHierarchy = state.hierarchy;
  const thisTlfType = getHierarchyTlfType(currentHierarchy.level);
  const relevantLibraries = action.clinicalLibrary.filter(
    (cl) =>
      cl.hierarchyId !== currentHierarchy.id &&
      getHierarchyTlfType(cl.hierarchyLevel) === thisTlfType &&
      updatedHierarchyNodes.some((hn) => hn.id === cl.hierarchyId),
  );

  const clinicalTemplates = relevantLibraries.reduce(
    (acc, clinicalLibrary) => mergeClinicalLibraryData(acc, clinicalLibrary),
    {
      clinicalDatasets: [...state.parentTemplates.clinicalDatasets],
      clinicalCodeLists: [...state.parentTemplates.clinicalCodeLists],
      clinicalValueLists: [...state.parentTemplates.clinicalValueLists],
      clinicalComments: [...state.parentTemplates.clinicalComments],
      clinicalMethods: [...state.parentTemplates.clinicalMethods],
      clinicalWhereClauses: [...state.parentTemplates.clinicalWhereClauses],
    },
  );

  return {
    ...state,
    hierarchyNodes: updatedHierarchyNodes,
    hierarchy: updatedHierarchy,
    isClinicalLibraryLoading: false,
    parentTemplates: {
      ...clinicalTemplates,
      domainReplacements: [...state.parentTemplates.domainReplacements],
      lists: [...state.parentTemplates.lists],
      rowGroups: [...state.parentTemplates.rowGroups],
      singles: [...state.parentTemplates.singles],
      analysisSets: [...state.parentTemplates.analysisSets],
      analysisMethods: [...state.parentTemplates.analysisMethods],
      dataSubsets: [...state.parentTemplates.dataSubsets],
      groupingFactors: [...state.parentTemplates.groupingFactors],
    },
  };
};
